import { Container } from '../../../styles/PageLayout'
import { InstallationReference } from '../../../domain/DTO'
import { FormLayout } from '../../../styles/FormLayout'
import React, { useState } from 'react'
import { PagedTable } from '@tomra/react-table'
import { SortableHeaderContent, useTableSorting } from '../../../components/assortment/TableColumnSort'

type Props = {
  installations: InstallationReference[]
  onComplete: (installationIds: string[]) => Promise<void>
}

export const InstallationViewTable = ({ installations, onComplete }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  const filteredInstallations = installations?.filter((installation: InstallationReference) =>
    installation.serialNumber.includes(searchTerm),
  )

  const sorting = useTableSorting<InstallationReference>(
    {
      'Serial Number': (installation) => installation.serialNumber,
      Chain: (installation) => installation.chainName ?? 'N/A',
      Location: (installation) => installation.locationName,
    },
    'Serial Number',
  )

  return (
    <div className="card" style={{ height: '80vh' }}>
      <div className="p-xl">
        <h1 className="mx-sm" style={{ fontSize: '2rem', marginBottom: '1rem' }}>{`Select installations`}</h1>
        <p>Select installations that will receive the same set of overrides as this installation</p>
        <Container style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div className={'inline-flex'}>
            <input
              title={'Search for a given serial number'}
              type="text"
              placeholder="Search by Serial Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              title={'Override selected installations'}
              className="btn btn-primary-dark mx-1 pr-3 text-center"
              disabled={isLoading}
              onClick={() => {
                alert(
                  'This will override the selected installations with the same set of overrides as this installation. Are you sure?',
                )
                setIsLoading(true)
                onComplete(selectedRows)
              }}
            >
              <FormLayout.LoadingButtonText loading={isLoading} text="Submit" loadingText="Submitting..." />
            </button>
          </div>
        </Container>
        <span style={{ paddingLeft: '10px', paddingRight: '10px' }}>
          Selected installation{selectedRows.length !== 1 ? 's' : ''} : {selectedRows.length}
        </span>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <PagedTable
            data={sorting.sort(filteredInstallations)}
            columnHeaders={[
              <div style={{ minWidth: '10em' }} key="select-header">
                <span>Select</span>
              </div>,
              <div style={{ minWidth: '10em' }} key="serial-number-header">
                <SortableHeaderContent name="Serial Number" currentSort={sorting.currentSort} />
              </div>,
              <div style={{ minWidth: '10em' }} key="chain-header">
                <SortableHeaderContent name="Chain" currentSort={sorting.currentSort} />
              </div>,
              <div style={{ minWidth: '10em' }} key="location-header">
                <SortableHeaderContent name="Location" currentSort={sorting.currentSort} />
              </div>,
            ]}
            rowFactory={(installation) => {
              return (
                <tr key={installation.installationId}>
                  <td style={{ padding: '10px' }}>
                    <input
                      title={'Select this installation'}
                      type="checkbox"
                      checked={selectedRows.includes(installation.installationId)}
                      onChange={(e) => {
                        const installationId = installation.installationId
                        setSelectedRows((prev) =>
                          e.target.checked ? [...prev, installationId] : prev.filter((id) => id !== installationId),
                        )
                      }}
                      style={{
                        backgroundColor: selectedRows.includes(installation.installationId) ? '#6eceb2' : 'white',
                        cursor: 'pointer',
                        width: '20px',
                        height: '20px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td style={{ backgroundColor: '#f9f9f9' }}>{installation.serialNumber}</td>
                  <td>{installation.chainName}</td>
                  <td style={{ backgroundColor: '#f9f9f9' }}>{installation.locationName}</td>
                </tr>
              )
            }}
            initialRecordsPerPage={10}
            recordsPerPageOptions={[10, 20, 50, 100]}
            className="table"
          />
        </div>
      </div>
    </div>
  )
}
